import { useState } from 'react';
import queryString from 'query-string';

import { SelectedItems } from '../components/general/gmb-local-posts/SearchBox';

import { GmbLocalPost, LocalPostTopicType, LocalPostTopicTypeJp } from '../models/gmb-local-post';
import { Pagination as PaginationModel, initializePagination } from '../models/pagination';

import { PagenationApiResponse } from '../requests/client';
import { ListGmbLocalPostParams as PartnerListGmbLocalPostParams } from '../requests/partner/gmb-local-posts';
import { ListGmbLocalPostParams as CompanyListGmbLocalPostParams } from '../requests/company/gmb-local-posts';

export type SearchParams = PartnerListGmbLocalPostParams | CompanyListGmbLocalPostParams;

type Props = {
  specifiedCompanyId?: number;
  specifiedGmbLocationId?: number;
  requestLocalPosts: (
    page: number,
    params?: SearchParams
  ) => Promise<PagenationApiResponse<GmbLocalPost[], PaginationModel>>;
};

type Queries = {
  currentPage: number;
  topicType: LocalPostTopicType;
  companyId?: number;
  gmbLocationId?: number;
};

type usePeriodsData = {
  isLoading: boolean;
  queries: {
    currentPage: number;
    topicType: LocalPostTopicType;
    companyId?: number;
    gmbLocationId?: number;
  };
  pagination: PaginationModel;
  gmbLocalPosts?: GmbLocalPost[];
  refresh(p: number, params?: SearchParams): Promise<void>;
  handleSearch(selectedItems?: SelectedItems): Promise<void>;
};

export const useLocalPostSearch = (props: Props): usePeriodsData => {
  const { specifiedCompanyId, specifiedGmbLocationId, requestLocalPosts } = props;

  const qs = queryString.parse(location.search);
  const currentPage = qs?.page ? parseInt(qs.page as string) : 1;
  const topicType = qs?.topicType ? (qs.topicType as LocalPostTopicType) : 'STANDARD';
  const companyId = specifiedCompanyId
    ? specifiedCompanyId
    : qs?.companyId
    ? parseInt(qs.companyId as string)
    : undefined;
  const gmbLocationId = specifiedGmbLocationId
    ? specifiedGmbLocationId
    : qs?.gmbLocationId
    ? parseInt(qs.gmbLocationId as string)
    : undefined;

  const vlidTopicType = Object.entries(LocalPostTopicTypeJp).some(
    (value: [key: LocalPostTopicType, jp: string]) => {
      const [key, _] = value;
      return key === topicType;
    }
  );

  const [queries, setQueries] = useState<Queries>({
    currentPage: currentPage,
    topicType: vlidTopicType ? topicType : 'STANDARD',
    companyId: companyId,
    gmbLocationId: gmbLocationId,
  });

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [pagination, setPagination] = useState<PaginationModel>(initializePagination);
  const [gmbLocalPosts, setGmbLocalPosts] = useState<GmbLocalPost[] | undefined>(undefined);

  const refresh = async (p: number, params?: SearchParams): Promise<void> => {
    setIsLoading(true);
    const v = await requestLocalPosts(p, params);
    setGmbLocalPosts(v.data);
    setPagination(v.pagination);
    setIsLoading(false);
  };

  const handleSearch = async (selectedItems?: SelectedItems): Promise<void> => {
    setQueries({
      currentPage: currentPage,
      companyId: selectedItems?.companyId,
      gmbLocationId: selectedItems?.gmbLocationId,
      topicType: selectedItems?.selectedTopicType || topicType,
    });
  };

  return {
    isLoading,
    queries,
    pagination,
    gmbLocalPosts,
    refresh,
    handleSearch,
  };
};
